// Single source of truth for membership plan status presentation —
// badge variant, tint, gradient, and chip classes. Mirrors the
// VOUCHER_STATUS_META pattern from features/vouchers/status.ts so callers
// have the same ergonomics across both CRM features.
//
// Only ACTIVE and INACTIVE are stored at the plan level. Individual
// customer enrollments have their own status (ACTIVE/EXPIRED/CANCELLED)
// which is handled separately on the Detail page's members tab.

import type { MembershipPlanStatus } from './types';

export interface MembershipStatusMeta {
  id: MembershipPlanStatus;
  label: string;
  /** Matches @bbpos/ui Badge StatusKey variants. */
  badgeVariant: 'success' | 'draft';
  tint: string;
  darkTint: string;
  iconBg: string;
  /** Hero gradient — `from-…` classes for the full page header. */
  gradient: string;
  chip: string;
  textAccent: string;
  description: string;
}

export const MEMBERSHIP_STATUS_META: Record<MembershipPlanStatus, MembershipStatusMeta> = {
  ACTIVE: {
    id: 'ACTIVE',
    label: 'Active',
    badgeVariant: 'success',
    tint: 'bg-amber-50',
    darkTint: 'dark:bg-amber-900/20',
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
    gradient: 'from-amber-600 via-yellow-500 to-orange-600',
    chip: 'bg-amber-400/25 text-amber-50 ring-1 ring-amber-300/40',
    textAccent: 'text-amber-700 dark:text-amber-300',
    description: 'Accepting new enrollments.',
  },
  INACTIVE: {
    id: 'INACTIVE',
    label: 'Inactive',
    badgeVariant: 'draft',
    tint: 'bg-neutral-100',
    darkTint: 'dark:bg-neutral-800/60',
    iconBg: 'bg-gradient-to-br from-neutral-400 to-neutral-600',
    gradient: 'from-neutral-500 via-slate-500 to-neutral-600',
    chip: 'bg-neutral-400/25 text-neutral-50 ring-1 ring-neutral-300/40',
    textAccent: 'text-neutral-600 dark:text-neutral-300',
    description: 'Not accepting new enrollments. Existing members retain benefits until expiry.',
  },
};

export function planStatusMeta(status: MembershipPlanStatus | undefined): MembershipStatusMeta {
  return MEMBERSHIP_STATUS_META[status ?? 'INACTIVE'];
}

/** Helper for deriving the status enum from the raw `is_active` boolean. */
export function toPlanStatus(isActive: boolean): MembershipPlanStatus {
  return isActive ? 'ACTIVE' : 'INACTIVE';
}
